import { withServerProps } from '@web/library/with-server-props';
import { useTranslation } from 'react-i18next';
import {
  IndexBreedersListScreen,
  IndexBreedersListScreenProps,
} from 'shared/screens/breeders/index-breeders-list';
import { Page } from '@web/components/page';
import { PageBuilder } from 'shared/library/page-builder';
import { Breeder, Paginator } from 'shared/@types';
import {
  BreedersFilter,
  getBreedersApiParams,
  parseBreedersFilter,
} from 'shared/state/breeders-filter';
import { apiRoute } from 'shared/library/api-route';
import { prepareDataForApi } from 'shared/library/api-client';
import { cities, City } from 'shared/library/cities';
import { breederCityRadiusInKm } from 'shared/screens/breeders/index-breeders-by-city';

interface IndexBreedersByCityScreenProps extends IndexBreedersListScreenProps {
  lockedCity: City;
}

export default function IndexBreedersByCity(props: IndexBreedersByCityScreenProps) {
  const { t } = useTranslation('screens');

  const title = t('breeders.index_breeders_list.locked_city_title', {
    city: props.lockedCity!.name,
  });

  const description = t('breeders.index_breeders_list.locked_city_description', {
    city: props.lockedCity!.name,
  });

  return (
    <Page {...props} register={(seo) => seo.setTitle(title).setDescription(description)}>
      <IndexBreedersListScreen {...props} />
    </Page>
  );
}

const pageBuilder = new PageBuilder<
  IndexBreedersByCityScreenProps,
  BreedersFilter & { city: string; cityData?: City }
>()
  .prepareParams((params) => {
    const city = cities.find((c: City) => c.slug === params.city);

    if (!city) {
      return { notFound: true };
    }

    return {
      ...params,
      cityData: city,
    };
  })
  .request(({ req, params }) => {
    const filter = parseBreedersFilter(params);

    return req<{ breeders: Paginator<Breeder> }>(apiRoute('breeders.index'), {
      with_details: true,
      ...prepareDataForApi(getBreedersApiParams(filter)),
      latitude: params.cityData!.latitude,
      longitude: params.cityData!.longitude,
      max_distance_in_km: breederCityRadiusInKm,
    }).then((res) => ({
      initialBreeders: res.breeders,
      initialFilter: filter,
    }));
  })
  .transformProps(({ props, params }) => ({
    ...props,
    lockedCity: params.cityData!,
  }))
  .deriveRouteParams((props) => ({
    city: props.lockedCity.slug,
  }));

export const getServerSideProps = withServerProps(pageBuilder);
